"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.generateNotableEvents = generateNotableEvents;
var _missionStateConstants = require("../constants/missionStateConstants");
var _notableEvents = require("../constants/notableEvents");
/**
 * generateSharedNotableEventsForScreenAction - generate notable events (ie. checkpoint and phases) if needed for a given screen action
 *
 * @param {ScreenAction<IdType>} screenAction - the screen action to generate shared notable events for
 * @param {number} atTime - the time that this action is ran in a condensed node
 *
 * @return {NotableEvent<IdType>[]}
 */
function generateSharedNotableEventsForScreenAction(screenAction, atTime, actionInstanceId) {
  const notableEvents = [];
  if (screenAction.checkpoint || screenAction.newPhase) {
    notableEvents.push({
      type: _notableEvents.NOTABLE_EVENT_TYPES.CHECKPOINT,
      actionId: screenAction._id,
      atTime,
      actionInstanceId
    });
  }
  if (screenAction.newPhase) {
    notableEvents.push({
      type: _notableEvents.NOTABLE_EVENT_TYPES.START_PHASE,
      actionId: screenAction._id,
      atTime,
      actionInstanceId
    });
  }
  return notableEvents;
}

// a mapping of action type to a function which will create notable events for the action
const ACTION_TO_NOTABLE_EVENT_GENERATOR = {
  ACTIVATE_STATION: (activateStationAction, atTime, actionInstanceId) => [{
    type: _notableEvents.NOTABLE_EVENT_TYPES.STATION_START,
    actionId: activateStationAction._id,
    stationId: activateStationAction.stationData.stationId,
    atTime,
    actionInstanceId
  }],
  DEACTIVATE_STATION: (deactivateStationAction, atTime, actionInstanceId) => [{
    type: _notableEvents.NOTABLE_EVENT_TYPES.STATION_END,
    actionId: deactivateStationAction._id,
    stationId: deactivateStationAction.stationId,
    atTime,
    actionInstanceId
  }],
  [_missionStateConstants.LITERACY_EVENT.SIMULATION_ACTION_TYPE]: (literacyEventAction, atTime, actionInstanceId) => [{
    type: _notableEvents.NOTABLE_EVENT_TYPES.LITERACY_EVENT,
    actionId: literacyEventAction._id,
    atTime,
    actionInstanceId
  }],
  MISSION_EFFECT: (missionEffectAction, atTime, actionInstanceId) => {
    const notableEvents = [];
    if (missionEffectAction.effectId === 'DEATH') {
      notableEvents.push({
        type: _notableEvents.NOTABLE_EVENT_TYPES.DEATH,
        actionId: missionEffectAction._id,
        atTime,
        actionInstanceId
      });
    }
    return notableEvents;
  },
  END_MISSION: (endMissionAction, atTime, actionInstanceId) => [{
    type: _notableEvents.NOTABLE_EVENT_TYPES.END_MISSION,
    actionId: endMissionAction._id,
    atTime,
    actionInstanceId
  }],
  VIDEO_SCREEN: generateSharedNotableEventsForScreenAction,
  IMAGE_SCREEN: generateSharedNotableEventsForScreenAction,
  NAVIGATION_SCREEN: (navigationScreenAction, atTime, actionInstanceId) => [{
    type: _notableEvents.NOTABLE_EVENT_TYPES.NAVIGATION_SCREEN,
    actionId: navigationScreenAction._id,
    atTime,
    actionInstanceId
  }, ...generateSharedNotableEventsForScreenAction(navigationScreenAction, atTime, actionInstanceId)],
  CULMINATING_MOMENT_SCREEN: (culminatingMomentAction, atTime, actionInstanceId) => [{
    type: _notableEvents.NOTABLE_EVENT_TYPES.CULMINATING_MOMENT,
    actionId: culminatingMomentAction._id,
    atTime,
    actionInstanceId
  }, ...generateSharedNotableEventsForScreenAction(culminatingMomentAction, atTime, actionInstanceId)],
  MAP_SCREEN: generateSharedNotableEventsForScreenAction,
  QUESTION_SLOT_SCREEN: (questionSlotScreenAction, atTime, actionInstanceId) => [{
    type: _notableEvents.NOTABLE_EVENT_TYPES.QUESTION_SLOT,
    actionId: questionSlotScreenAction._id,
    atTime,
    actionInstanceId
  }, ...generateSharedNotableEventsForScreenAction(questionSlotScreenAction, atTime, actionInstanceId)],
  COLLABORATIVE_CULMINATING_MOMENT_SCREEN: (collaborativeCulminatingCulminatingMomentAction, atTime, actionInstanceId) => [{
    type: _notableEvents.NOTABLE_EVENT_TYPES.COLLABORATIVE_CULMINATING_MOMENT,
    actionId: collaborativeCulminatingCulminatingMomentAction._id,
    atTime: atTime + collaborativeCulminatingCulminatingMomentAction.editingTime,
    actionInstanceId
  }, ...generateSharedNotableEventsForScreenAction(collaborativeCulminatingCulminatingMomentAction, atTime, actionInstanceId)],
  MAP_CHANGE: (mapChangeAction, atTime, actionInstanceId) => {
    if (mapChangeAction.mapId == null) {
      return [];
    }
    return [{
      type: _notableEvents.NOTABLE_EVENT_TYPES.SET_MAP,
      actionId: mapChangeAction._id,
      atTime,
      actionInstanceId
    }];
  }
};

/**
 * generateNotableEvents - generate a list of notable events for a list of given actions
 *
 * @param {EventResultWithStartTimeData<IdType>} actionData - the action list to generate the notable events for
 * @param {ActionMap<IdType>} actionMap - a mapping of action id to their action
 * @param {boolean} isInStartingNode - true if the actionData comes from the condensed node ran at the start of the simulation
 * @param {IdType} initialActionId - the id of the starting action in the simulation
 *
 * @return {Array<NotableEvent<IdType>>} - an array of notable events
 */
function generateNotableEvents(actionData, actionMap, isInStartingNode, initialActionId) {
  const notableEvents = [];
  let nextActionInstanceId = 1;
  if (isInStartingNode) {
    notableEvents.push({
      type: _notableEvents.NOTABLE_EVENT_TYPES.START_MISSION,
      actionId: initialActionId,
      atTime: 0,
      actionInstanceId: nextActionInstanceId++
    });
  }
  for (let actionInfo of actionData) {
    let action = actionMap[String(actionInfo.actionId)];
    if (!action || !ACTION_TO_NOTABLE_EVENT_GENERATOR.hasOwnProperty(action.type)) {
      continue;
    }
    // $FlowFixMe[underconstrained-implicit-instantiation] the existence for this action handler is checked above (Note: flow reports this error as loosing the IdType generic)
    const notableEvent = ACTION_TO_NOTABLE_EVENT_GENERATOR[action.type](
    // $FlowFixMe[prop-missing]
    // $FlowFixMe[incompatible-call] ASSUMPTION: if the action type is correct, then the action is of the correct type for the handler
    action, actionInfo.startTime, nextActionInstanceId++);
    if (!notableEvent.length) {
      continue;
    }
    notableEvents.push(...notableEvent);
  }
  return notableEvents;
}